"use client";

import { Button } from "@/components/new/button";
import { Pencil, Star } from "lucide-react";
import { useState } from "react";
import ProductReviewForm from "./review/product-review-form";
import { Stars } from "@/components/common/feedback/feedback-stars";
import Modal from "@/components/ui/modal";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { TCustomUserSession } from "@/types/app";
import { TProduct, TRatingData, TProductReview } from "@/types/productv2";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ModalType } from "@/types/modal-type";

type TProductRatingProps = {
	product: TProduct;
	ratingData: TRatingData | undefined;
	productSlug: string;
	reviews: TProductReview[];
};

export default function ProductRating({
	product,
	ratingData,
	productSlug,
	reviews,
}: TProductRatingProps) {
	const [showReviewForm, setShowReviewForm] = useState(false);
	const { isLoggedIn, user } = useAuth();
	const { openModal } = useModal();
	const queryClient = useQueryClient();

	const getRatingPercent = (count: number) => {
		if (!ratingData?.noOfRatings) return "0%";
		return `${((count / ratingData.noOfRatings) * 100).toFixed(0)}%`;
	};

	const ratingCounts = [
		{ stars: 5, count: ratingData?.ratingCounts?.[5] || 0 },
		{ stars: 4, count: ratingData?.ratingCounts?.[4] || 0 },
		{ stars: 3, count: ratingData?.ratingCounts?.[3] || 0 },
		{ stars: 2, count: ratingData?.ratingCounts?.[2] || 0 },
		{ stars: 1, count: ratingData?.ratingCounts?.[1] || 0 },
	];

	const handleWriteReviewClick = () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}

		// Check if the user has already submitted a review
		const hasSubmittedReview = reviews.some((review) => review.user.id === user?.id);
		if (hasSubmittedReview) {
			toast.error("You have already submitted a review for this product.");
			return;
		}

		setShowReviewForm(true);
	};

	return (
		<>
			<div className="my-8 grid grid-cols-1 gap-8 md:grid-cols-3">
				<div className="flex flex-col items-center justify-center">
					{ratingData?.totalRating && parseFloat(ratingData.totalRating) > 0 ? (
						<div className="mb-2 text-7xl font-bold">{ratingData.totalRating}</div>
					) : (
						<span className="mb-2 text-2xl font-bold">No Rating</span>
					)}

					<div className="mb-1">
						<Stars
							readOnly
							rating={parseFloat(ratingData?.totalRating || "0")}
							classNames={{ star: "w-8 h-8", starContainer: "w-9 h-9" }}
						/>
					</div>

					<div className="mb-6 font-medium">{ratingData?.noOfRatings || 0} Reviews</div>

					<div className="w-full space-y-2">
						{ratingCounts.map((rating) => (
							<div key={rating.stars} className="flex items-center">
								<div className="mr-2 flex w-8 items-center text-right">
									<span className="mr-1">{rating.stars}</span>
									<Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
								</div>

								<div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
									<div
										className="h-full rounded-full bg-yellow-400"
										style={{
											width: getRatingPercent(rating.count),
										}}
									></div>
								</div>

								<div className="ml-2 w-8 text-left">{rating.count}</div>
							</div>
						))}
					</div>
				</div>

				{/* Custom Ratings */}
				<div className="flex flex-col items-center justify-center">
					<div className="flex flex-col justify-center gap-6">
						{[
							{ label: "Customer Support", value: parseFloat(ratingData?.supportRating || "0") },
							{ label: "Functionality", value: parseFloat(ratingData?.functionalityRating || "0") },
							{ label: "Value for Money", value: parseFloat(ratingData?.valueRating || "0") },
						].map((ratingItem) => (
							<div key={ratingItem.label} className="flex flex-col">
								<div className="mb-1 text-lg font-medium">{ratingItem.label}</div>
								<div className="flex gap-2">
									<div className="mt-0.5 w-min">
										<Stars
											readOnly
											rating={ratingItem.value}
											classNames={{ star: "w-5 h-5", starContainer: "w-6 h-6" }}
										/>
									</div>
									<div className="font-medium">{ratingItem.value.toFixed(1)}</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Add Review CTA */}
				<div className="flex flex-col items-center justify-center">
					<h3 className="mb-6 text-center text-xl font-medium">
						Your opinion makes the product better. Tell us your experience!
					</h3>

					<Button
						disabled={ratingData === undefined}
						onClick={handleWriteReviewClick}
						title={ratingData === undefined ? "Loading review data..." : undefined}
					>
						<Pencil className="mr-2 h-4 w-4" />
						Write a Review
					</Button>
				</div>
			</div>

			<Modal
				isOpen={showReviewForm}
				title="Write a Review"
				onClose={() => setShowReviewForm(false)}
				className="max-h-[90vh] max-w-[800px] overflow-y-auto"
			>
				{user?.id && (
					<ProductReviewForm
						productId={product.id}
						productSlug={productSlug}
						productName={product.name}
						companyName={product.companyName}
						onClose={() => setShowReviewForm(false)}
						onSuccess={() => {
							queryClient.invalidateQueries(["productRating", productSlug]);
						}}
						user={user as TCustomUserSession}
					/>
				)}
			</Modal>
		</>
	);
}
